import { useState } from "react"
import cc from "classcat"
import { useController } from "react-hook-form"
import { Combobox } from "@headlessui/react"
import { ChevronUpDownIcon } from "@heroicons/react/20/solid"
import useTranslations from "hooks/useTranslations"
import { Label } from "components/atoms/form"
import { toDateTimeShortFormat } from "utils/date"

import type  { Control } from "react-hook-form"

const ComboboxOption = (props) => (
  <Combobox.Option
    {...props}
    className={({ active }) =>
      cc([
        active ? "bg-teal-100 text-teal-900" : "text-zinc-900",
        "relative cursor-default select-none px-3 py-2 text-sm",
      ])
    }
  />
)

type Item = { title: string; id: string; time?: string }

interface ComboSelectProps {
  items: Item[]
  label: string
  name: string
  required: boolean
  control: Control
}

export const ComboSelect = (props: ComboSelectProps) => {
  const {
    field: { value, onChange, ref },
    fieldState: { error },
  } = useController({
    ...props,
    rules: {
      required: props.required,
    },
  })
  const { formAdditionalValue } = useTranslations()
  const { items, label, required } = props
  const [selectedItem, setSelectedItem] = useState(value)
  const [query, setQuery] = useState("")

  const filteredItems =
    query === ""
      ? items
      : items.filter((item) => {
          return item.title.toLowerCase().includes(query.toLowerCase())
        })

  return (
    <div className="block w-full sm:inline-flex">
      <div className="w-full">
        <Label label={label} required={required} error={error} />
        <Combobox
          onChange={(e) => {
            setSelectedItem(e)
            onChange(e)
          }}
          value={selectedItem}
        >
          <div className="relative mt-1">
            <Combobox.Input
              displayValue={(item: Item) => item?.title}
              onChange={(event) => setQuery(event.target.value)}
              autoComplete="off"
              ref={ref}
              className="relative z-0 inline-flex w-full h-10 px-3 py-2 pl-3 pr-8 text-sm leading-normal text-left bg-white border rounded-lg appearance-none border-zinc-300 text-zinc-800 focus:border-0 focus:ring-2 focus:ring-blue-600"
            >
              {value?.time && (
                <span className="mr-1 font-bold shrink-0">
                  {toDateTimeShortFormat(value.time)}
                </span>
              )}
            </Combobox.Input>
            <Combobox.Button className="absolute inset-y-0 flex items-center right-1 focus:outline-none">
              <ChevronUpDownIcon
                className="w-4 h-4 text-zinc-400"
                aria-hidden="true"
              />
            </Combobox.Button>
            <Combobox.Options className="absolute z-10 w-full py-1 mt-1 overflow-auto bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none">
              {query.length > 0 && (
                <ComboboxOption value={{ id: query, title: query }}>
                  {formAdditionalValue} "{query}"
                </ComboboxOption>
              )}
              {filteredItems.map((item) => (
                <ComboboxOption
                  key={item.id}
                  value={item}
                  className={({ active }) =>
                    cc([
                      active ? "bg-teal-100 text-teal-900" : "text-zinc-900",
                      "relative cursor-default select-none px-3 py-2 text-sm",
                    ])
                  }
                >
                  <span className="block text-left truncate">
                    <span className="font-bold">
                      {item.time && toDateTimeShortFormat(item.time)}
                    </span>{" "}
                    {item.title}
                  </span>
                </ComboboxOption>
              ))}
            </Combobox.Options>
          </div>
        </Combobox>
      </div>
    </div>
  )
}
